import type { AxiosResponse } from 'axios';
import { isAxiosError } from 'axios';
import { each } from 'lodash';
import type { Component, VNode } from 'vue';
import { computed, ref } from 'vue';

export type UiError = string | Component | VNode;

type ErrorOutput = {
  errors: UiError[];
  message: UiError;
  isValidationError: boolean;
};

export const isHttpError = isAxiosError;

/**
 * @deprecated Use useErrors instead
 */
export const useApiErrors = (messageMap?: Record<string, UiError>) => {
  const errors = ref<Record<string, UiError[]>>({});

  const first = computed(() => {
    if (!flattened.value.length) {
      return undefined;
    }

    return flattened.value[0];
  });

  const flattened = computed(() => {
    const mapped: UiError[] = [];

    each(errors.value, (error) => {
      if (error) {
        if (Array.isArray(error)) {
          mapped.push(...error);
        } else {
          mapped.push(error as UiError);
        }
      }
    });

    return mapped;
  });

  const clear = () => {
    errors.value = {};
  };

  const set = (errs: Record<string, UiError[]>) => {
    errors.value = errs;
  };

  const from = async (
    response: Response | AxiosResponse | undefined,
    fallback?: string,
  ): Promise<ErrorOutput> => {
    const defaultMessage = fallback || 'Something went wrong. Please try again later.';

    let errors;
    let message;

    if (!response) {
      // Handle cases where response is undefined (e.g., network errors)
      set({ default: [defaultMessage] });

      return {
        errors: flattened.value,
        isValidationError: false,
        message: first.value || defaultMessage,
      };
    }

    if (response instanceof Response) {
      const json = await response.json();

      errors = json.errors;
      message = json.message;
    } else {
      errors = response.data.errors;
      message = response.data.message;
    }

    const normalized: Record<string, UiError[]> = {};

    if (Array.isArray(errors)) {
      const messages = errors
        .map((e: unknown) => (typeof e === 'string' ? e : (e as Record<string, unknown>)?.message))
        .filter((m: unknown): m is string => typeof m === 'string');

      if (messages.length) {
        normalized.default = messages;
      }
    } else if (errors && typeof errors === 'object') {
      each(errors, (value: unknown, key: string) => {
        if (Array.isArray(value)) {
          normalized[key] = value;
        } else if (typeof value === 'string') {
          normalized[key] = [value];
        }
      });
    }

    if (message) {
      const mapped = messageMap?.[message] || message;

      if (normalized.default) {
        normalized.default.unshift(mapped);
      } else {
        normalized.default = [mapped];
      }
    }

    set(normalized);

    return {
      errors: flattened.value,
      isValidationError: [400, 422].includes(response.status),
      message: first.value || defaultMessage,
    };
  };

  const has = (key: string) => {
    return errors.value[key] !== undefined;
  };

  return {
    errors,
    first,
    set,
    clear,
    flattened,
    from,
    has,
  };
};
